import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { fetchSearchNotes } from '@/services/noteGoApi/noteGoApiComponents';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { getTagName } from '@/utils/tagUtils';
import { errorReporter } from '@/services/errorReportingService';

interface UseTagCountsOptions {
  /**
   * Filter which notes to count tags from:
   * - false: Only user's own notes (not shared by others) - for "My Notes"
   * - true: Only notes shared by others - for "Shared with me"
   * - undefined: All notes (both owned and shared)
   */
  shared?: boolean;
  /**
   * Restrict counts to favorited notes. CONTRACT #3 — counts MUST be scoped to
   * the notes actually visible in the current view. On /my-notes/favorites the
   * list is filtered by `favorite: true`, so unscoped counts would advertise
   * "meeting (12)" and return 1 result on click.
   * `undefined` counts every note regardless of favorite status.
   */
  favorite?: boolean;
  /**
   * Defer the fetch until the consumer actually needs the counts (e.g. the
   * tag filter UI is opened). Defaults to true for backward compatibility.
   */
  enabled?: boolean;
}

export const useTagCounts = (options?: UseTagCountsOptions) => {
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userToken, userData } = useAuthStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const shared = options?.shared;
  const favorite = options?.favorite;
  const enabled = options?.enabled ?? true;

  const refetch = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !userToken || !userData?.id) {
      return;
    }

    let mounted = true;

    const fetchAllTagCounts = async () => {
      setIsLoading(true);
      setError(null);
      const counts: Record<string, number> = {};
      let cursor: string | null = null;

      try {
        do {
          const resp = await fetchSearchNotes({
            headers: { authorization: `Bearer ${userToken}` },
            queryParams: {
              limit: 1000,
              cursor,
              // Pass shared/favorite through to the backend so counts are filtered
              // at the source and match the note list exactly (CONTRACT #3).
              ...(shared !== undefined && { shared }),
              ...(favorite !== undefined && { favorite }),
            },
          });

          (resp.notes as NoteOut[]).forEach((note) => {
            note.tags?.forEach((tag) => {
              const tagName = getTagName(tag);
              if (tagName) {
                // Normalize to lowercase to match backend normalization
                const normalizedTag = tagName.trim().toLowerCase();
                counts[normalizedTag] = (counts[normalizedTag] || 0) + 1;
              }
            });
          });

          cursor = resp.next_cursor;
        } while (cursor);

        if (mounted) {
          setTagCounts(counts);
        }
      } catch (err) {
        errorReporter.expected('Failed to fetch tag counts', err, { feature: 'notes', operation: 'useTagCounts' });
        if (mounted) {
          setError('Failed to load tags');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAllTagCounts();

    return () => {
      mounted = false;
    };
  }, [userToken, userData?.id, shared, favorite, enabled, refreshKey]);

  return { tagCounts, isLoading, error, refetch };
};
